import { AddonInputType } from "@/types";
import { formatCurrency, type Currency } from "@/lib/utils/currency";

/** Mirrors backend `interpolateAddonDisplayTags` — keep tag names in sync. */
export function interpolateAddonDisplayTags(
	template: string,
	replacements: { time?: string; price?: string; estimatedDeliveryDays?: string },
): string {
	let out = template;
	if (replacements.estimatedDeliveryDays !== undefined) {
		out = out.replace(
			/\{estimated delivery days of the package\}/gi,
			replacements.estimatedDeliveryDays,
		);
	}
	if (replacements.time !== undefined) {
		out = out.replace(/\{time\}/gi, replacements.time);
	}
	if (replacements.price !== undefined) {
		out = out.replace(/\{price\}/gi, replacements.price);
	}
	return out;
}

/** Resolve addon description templates for display (e.g. express delivery subline). */
export function resolveAddonDescriptionDisplay(
	description: string,
	currency: Currency,
	unitAmount: number,
): string {
	if (!/\{price\}|\{time\}|\{estimated delivery days of the package\}/i.test(description)) {
		return description;
	}
	return interpolateAddonDisplayTags(description, {
		price: formatCurrency(unitAmount, currency),
	});
}

function isNumberAddonInputType(inputType: unknown): boolean {
	if (inputType === AddonInputType.number) return true;
	return typeof inputType === "string" && inputType.toUpperCase() === AddonInputType.number;
}

/**
 * Text after the price for number add-ons (e.g. "per each hour").
 * Legacy listings stored only the noun ("hour"); those still render as "per each hour".
 */
export function resolveNumberAddonPricingSuffix(rawUnit: string | undefined | null): string {
	const unit = typeof rawUnit === "string" ? rawUnit.trim() : "";
	if (!unit) return "per each";
	if (/\bper\b/i.test(unit)) return unit;
	return `per each ${unit}`;
}

/** Number add-on: `{price} {suffix}` — suffix is editable in admin Unit type (includes "per each"). */
export function formatNumberAddonPerUnitSubline(
	addon: Pick<{ inputType?: unknown; unit?: unknown }, "inputType" | "unit">,
	unitAmount: number,
	currency: Currency,
): string {
	const price = formatCurrency(unitAmount, currency);
	if (!isNumberAddonInputType(addon.inputType)) return `${price} per each`;
	return `${price} ${resolveNumberAddonPricingSuffix(
		typeof addon.unit === "string" ? addon.unit : "",
	)}`;
}
